<script setup lang="ts">
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { storeSignupEmail } from '@/signupEmail'

const route = useRoute()

const email = ref('')
const password = ref('')

const { errors, inFlight, emailVerificationRequired, login } = useAuth()

async function handleLogin() {
  const next = route.query.next as string
  const redirectUrl = next || '/dashboard/'
  await login(email.value, password.value, redirectUrl)
  password.value = ''
}

function storeEmailForVerification() {
  storeSignupEmail(email.value)
}

function getSignupUrl() {
  const next = route.query.next as string
  if (next) {
    const url = new URL(next, window.location.origin)
    const attributionParams = new URLSearchParams()
    url.searchParams.forEach((value, key) => {
      // Forward both the internal handoff (signup_*) and external campaigns (utm_*);
      // SignupInitial decides precedence between them.
      if (key.startsWith('signup_') || key.startsWith('utm_')) {
        attributionParams.set(key, value)
      }
    })
    if (attributionParams.toString()) {
      return `/signup/?${attributionParams.toString()}`
    }
  }
  return '/signup/'
}
</script>

<template>
  <main class="col mt-3">
    <div class="row">
      <div class="col-md-6 offset-md-3 my-3">
        <div class="alert-slot">
          <div
            v-if="errors?.non_field_errors"
            class="alert alert-danger"
            role="alert"
          >
            {{ errors.non_field_errors[0] }}
          </div>
        </div>
        <h3>Log In to Stadia Maps</h3>
        <form @submit.prevent="handleLogin" id="login-form">
          <div class="mb-3">
            <div class="form-group">
              <label for="email" class="form-label">Email</label>
              <input
                type="email"
                class="form-control"
                id="email"
                v-model="email"
                required
                :class="{ 'is-invalid': errors?.email }"
              />
              <div class="invalid-feedback">
                {{ errors?.email?.[0] }}
              </div>
            </div>
          </div>

          <div class="mb-3">
            <div class="form-group">
              <label for="password" class="form-label">Password</label>
              <input
                type="password"
                class="form-control"
                id="password"
                required
                v-model="password"
                :class="{ 'is-invalid': errors?.password }"
              />
              <div class="invalid-feedback">
                {{ errors?.password?.[0] }}
              </div>
            </div>
          </div>

          <button type="submit" class="btn btn-primary" :disabled="inFlight">
            <span
              v-if="inFlight"
              class="spinner-border spinner-border-sm"
              role="status"
            />
            <template v-else> Login </template>
          </button>
        </form>

        <hr />

        <p>
          Need an account?
          <a :href="getSignupUrl()">Sign Up</a> and get 14 days on Stadia Maps
          Professional free. No credit card required.
          <br />
          Forgot your password?
          <!-- Plain links on purpose: soft route swaps bill their layout shift to the landing URL's field CLS. -->
          <a href="/password-reset/">Reset it</a>.
          <br />
          <template v-if="emailVerificationRequired">
            Need to verify your email?
            <!-- The click handler stashes the email for the verification page; the navigation stays a full load. -->
            <a href="/signup/verify-email/" @click="storeEmailForVerification">
              Check verification status
            </a>
          </template>
        </p>
      </div>
    </div>
  </main>
</template>

<style scoped lang="scss">
h3 {
  margin-bottom: 2.5rem;
}
</style>
